import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    ApFeedProducts,
    ApFeedRequest,
    ApFeedResponse,
    ApFeedUpdateRequest,
    ApProduct,
    DataminrFeedLink,
    DataminrFeedLinks,
    DataminrFeedRequest,
    DataminrFeedResponse,
    DataminrFeedUpdateRequest,
    DiscoverProviderResponse,
    FtpFeedRequest,
    FtpFeedResponse,
    FtpFeedUpdateRequest,
    WireCredentials,
    WireCredentialsRequest,
} from '@discover/store/models/feed.provider.models';
import { Store } from '@ngrx/store';
import { WireFeedTypes } from '@src/suite/common/enums/wire-feed-types';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { catchError, map, Observable, throwError } from 'rxjs';

import { selectJWTToken } from '../store/navigation/selectors/application.selectors';
import { GlobalTablesService } from './global-tables-service';
import { NomWebServicesJson } from './nom-web-services-json';

@Injectable({ providedIn: 'root' })
export class FeedProviderService {
    nomWebServicesJson = inject(NomWebServicesJson);
    globalTablesService = inject(GlobalTablesService);

    private store = inject(Store);
    private languageSettings = this.store.selectSignal(selectI18N);

    readonly token = this.store.selectSignal(selectJWTToken);

    public createWireCredentials(credentials: WireCredentialsRequest): Observable<WireCredentials> {
        let response: Observable<WireCredentials> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials?feedType=' + credentials.feedType;

        try {
            response = this.nomWebServicesJson
                .discoverServicePost<WireCredentials>(url, credentials, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        if (!res.body.feedType) res.body.feedType = credentials.feedType;
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getWireCredentials(feedType: WireFeedTypes, credentialsId?: string): Observable<WireCredentials[]> {
        let response: Observable<WireCredentials[]> = null;
        let url: string = null;
        if (feedType) {
            url = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials?feedType=' + feedType;
        }
        if (credentialsId) {
            if (url) {
                url = url + '&id=' + credentialsId;
            } else {
                url = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials?id=' + credentialsId;
            }
        }

        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<WireCredentials[]>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public updateWireCredentials(feedType: WireFeedTypes, credentials: WireCredentials, credentialsChanged: boolean): Observable<WireCredentials> {
        let response: Observable<WireCredentials> = null;
        let url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials/credentials?feedType=' + feedType;
        if (!credentialsChanged) {
            url = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials?feedType=' + feedType;
            delete credentials.credentials;
        }

        try {
            response = this.nomWebServicesJson
                .discoverServicePut<WireCredentials>(url, credentials, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public deleteWireCredentials(credentialsId: string, type: WireFeedTypes): Observable<WireCredentials> {
        let response: Observable<WireCredentials> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-credentials/' + credentialsId + '?feedType=' + type;

        try {
            response = this.nomWebServicesJson
                .discoverServiceDelete<WireCredentials>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getApFeedProducts(id: string): Observable<ApProduct[]> {
        let response: Observable<ApProduct[]> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds/products?ID=' + id;
        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<ApFeedProducts>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        const products: ApProduct[] = [];
                        res.body.data.plans.forEach(plan => {
                            plan.entitlements.forEach(product => {
                                if (!products.find(p => p.id === product.id)) {
                                    products.push(product);
                                }
                            });
                        });
                        return products;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public createAPFeed(feed: ApFeedRequest): Observable<ApFeedResponse> {
        let response: Observable<ApFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds';

        try {
            response = this.nomWebServicesJson
                .discoverServicePost<ApFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public updateAPFeed(feed: ApFeedUpdateRequest): Observable<ApFeedResponse> {
        let response: Observable<ApFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds';

        try {
            response = this.nomWebServicesJson
                .discoverServicePut<ApFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getAPFeeds(): Observable<ApFeedResponse[]> {
        let response: Observable<ApFeedResponse[]> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds?scope=0';
        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<ApFeedResponse[]>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            console.warn(error);
            return throwError(() => error);
        }
        return response;
    }

    public deleteAPFeed(feedId: string, scopes: number[]): Observable<ApFeedResponse> {
        let response: Observable<ApFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ap-feeds/' + feedId + '?scope=' + scopes[0];

        try {
            response = this.nomWebServicesJson
                .discoverServiceDelete<ApFeedResponse>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getApFeed(feedId: string): Observable<ApFeedResponse[]> {
        let response: Observable<ApFeedResponse[]> = null;
        const url = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/ap-feeds?id=${feedId}&scope=0`;

        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<ApFeedResponse[]>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public createFTPFeed(feed: FtpFeedRequest): Observable<FtpFeedResponse> {
        let response: Observable<FtpFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ftp-feeds';

        try {
            response = this.nomWebServicesJson
                .discoverServicePost<FtpFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public updateFTPFeed(feed: FtpFeedUpdateRequest): Observable<FtpFeedResponse> {
        let response: Observable<FtpFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ftp-feeds';

        try {
            response = this.nomWebServicesJson
                .discoverServicePut<FtpFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getFTPFeeds(): Observable<FtpFeedResponse[]> {
        let response: Observable<FtpFeedResponse[]> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ftp-feeds?scope=0';
        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<FtpFeedResponse[]>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public deleteFTPFeed(feedId: string, scopes: number[]): Observable<FtpFeedResponse> {
        let response: Observable<FtpFeedResponse> = null;
        const url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/ftp-feeds/' + feedId + '?scope=' + scopes[0];

        try {
            response = this.nomWebServicesJson
                .discoverServiceDelete<FtpFeedResponse>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getFTPFeed(feedId: string): Observable<FtpFeedResponse[]> {
        let response: Observable<FtpFeedResponse[]> = null;
        const url = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/ftp-feeds?id=${feedId}&scope=0`;

        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<FtpFeedResponse[]>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getProviders(type?: string): Observable<DiscoverProviderResponse[]> {
        let response: Observable<DiscoverProviderResponse[]> = null;
        let url: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/providers';
        if (type) url += '?type=' + type;

        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<DiscoverProviderResponse[]>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getDataminrFeedLinks(id: string): Observable<DataminrFeedLink[]> {
        let response: Observable<DataminrFeedLink[]> = null;
        const url = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/dataminr-feeds/links?ID=${id}`;
        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<DataminrFeedLinks>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        const links: DataminrFeedLink[] = [];
                        if (res.body && res.body.watchLists) {
                            res.body.watchLists?.topics?.forEach(topic => {
                                links.push(topic);
                            });
                            res.body.watchLists?.company?.forEach(company => {
                                links.push(company);
                            });
                            res.body.watchLists?.custom?.forEach(custom => {
                                links.push(custom);
                            });
                            res.body.watchLists?.cyber?.forEach(cyber => {
                                links.push(cyber);
                            });
                        } else {
                            console.error('Invalid response format from Dataminr API:', res.body);
                        }
                        return links;
                    }),
                    catchError(error => {
                        if (error.status === 401) {
                            this.nomWebServicesJson.modalService.openAlert({
                                title: this.languageSettings()[I18nKeys.PSTCreateDataminrLinksErrorTitle],
                                content: this.languageSettings()[I18nKeys.PSTCreateDataminrLinksErrorMessage],
                                okString: this.languageSettings()[I18nKeys.PSTWebOkButtonLabel],
                            });
                        }

                        console.error('Error fetching Dataminr links:', error);
                        return throwError(() => error);
                    })
                );
        } catch (error) {
            console.error('Exception in getDataminrFeedLinks:', error);
            return throwError(() => error);
        }
        return response;
    }

    public createDataminrFeed(feed: DataminrFeedRequest): Observable<DataminrFeedResponse> {
        let response: Observable<DataminrFeedResponse> = null;
        const url = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/dataminr-feeds`;

        try {
            response = this.nomWebServicesJson
                .discoverServicePost<DataminrFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    }),
                    catchError(error => {
                        if (error.status === 409) {
                            this.nomWebServicesJson.modalService.openAlert({
                                title: this.languageSettings()[I18nKeys.PSTCreateFeedErrorTitle],
                                content: this.languageSettings()[I18nKeys.PSTCreateFeedErrorMessage],
                                okString: this.languageSettings()[I18nKeys.PSTWebOkButtonLabel],
                            });
                        }
                        return throwError(() => error);
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public updateDataminrFeed(feed: DataminrFeedUpdateRequest): Observable<DataminrFeedResponse> {
        let response: Observable<DataminrFeedResponse> = null;
        const url = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/dataminr-feeds`;

        try {
            response = this.nomWebServicesJson
                .discoverServicePut<DataminrFeedResponse>(url, feed, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    }),
                    catchError(error => {
                        if (error.status === 409) {
                            this.nomWebServicesJson.modalService.openAlert({
                                title: this.languageSettings()[I18nKeys.PSTCreateFeedErrorTitle],
                                content: this.languageSettings()[I18nKeys.PSTCreateFeedErrorMessage],
                                okString: this.languageSettings()[I18nKeys.PSTWebOkButtonLabel],
                            });
                        }
                        return throwError(() => error);
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public getDataminrFeeds(): Observable<DataminrFeedResponse[]> {
        let response: Observable<DataminrFeedResponse[]> = null;
        const url = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/dataminr-feeds?scope=0`;
        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<DataminrFeedResponse[]>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            console.warn(error);
            return throwError(() => error);
        }
        return response;
    }

    public getDataminrFeed(feedId: string): Observable<DataminrFeedResponse[]> {
        let response: Observable<DataminrFeedResponse[]> = null;
        const url = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/dataminr-feeds?id=${feedId}&scope=0`;

        try {
            response = this.nomWebServicesJson
                .discoverServiceGet<DataminrFeedResponse[]>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }

    public deleteDataminrFeed(feedId: string, scopes: number[]): Observable<DataminrFeedResponse> {
        let response: Observable<DataminrFeedResponse> = null;
        const url = `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/dataminr-feeds/${feedId}?scope=${scopes[0]}`;

        try {
            response = this.nomWebServicesJson
                .discoverServiceDelete<DataminrFeedResponse>(url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.token(),
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                })
                .pipe(
                    map(res => {
                        return res.body;
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
        return response;
    }
}
